import React from 'react';
import PropTypes from 'prop-types';

const GaugeMeter = ({
  value,
  maxValue = 5,
  label,
  band = 'Neutral',
  color = 'blue',
  size = 'md',
  showPercentage = false,
  className = ''
}) => {
  const percentage = Math.min(100, Math.max(0, (value / maxValue) * 100));

  const sizeConfig = {
    sm: { container: 'w-24 h-24', strokeWidth: 6, radius: 40, fontSize: 'text-lg', labelSize: 'text-xs', bandSize: 'text-xs' },
    md: { container: 'w-32 h-32', strokeWidth: 8, radius: 50, fontSize: 'text-2xl', labelSize: 'text-sm', bandSize: 'text-xs' },
    lg: { container: 'w-40 h-40', strokeWidth: 10, radius: 60, fontSize: 'text-3xl', labelSize: 'text-base', bandSize: 'text-sm' }
  };

  const colorConfig = {
    green: { stroke: '#10B981', bg: '#D1FAE5', text: 'text-emerald-600', badgeBg: 'bg-emerald-50', badgeText: 'text-emerald-700' },
    blue: { stroke: '#3B82F6', bg: '#DBEAFE', text: 'text-blue-600', badgeBg: 'bg-blue-50', badgeText: 'text-blue-700' },
    yellow: { stroke: '#EAB308', bg: '#FEF9C3', text: 'text-yellow-600', badgeBg: 'bg-yellow-50', badgeText: 'text-yellow-700' },
    amber: { stroke: '#F59E0B', bg: '#FEF3C7', text: 'text-amber-600', badgeBg: 'bg-amber-50', badgeText: 'text-amber-700' },
    orange: { stroke: '#F97316', bg: '#FFEDD5', text: 'text-orange-600', badgeBg: 'bg-orange-50', badgeText: 'text-orange-700' },
    red: { stroke: '#EF4444', bg: '#FEE2E2', text: 'text-red-500', badgeBg: 'bg-red-50', badgeText: 'text-red-700' },
    gray: { stroke: '#64748B', bg: '#F1F5F9', text: 'text-neutral-600', badgeBg: 'bg-neutral-100', badgeText: 'text-neutral-700' },
    purple: { stroke: '#8B5CF6', bg: '#EDE9FE', text: 'text-purple-600', badgeBg: 'bg-purple-50', badgeText: 'text-purple-700' }
  };

  const config = sizeConfig[size];
  const colorSet = colorConfig[color] || colorConfig.blue;

  const circumference = 2 * Math.PI * config.radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  const displayValue = typeof value === 'number'
    ? (showPercentage ? `${Math.round(percentage)}%` : value.toFixed(2))
    : value;

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div className={`relative ${config.container}`}>
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 120 120">
          <circle
            cx="60" cy="60" r={config.radius}
            fill="none" stroke={colorSet.bg}
            strokeWidth={config.strokeWidth}
          />
          <circle
            cx="60" cy="60" r={config.radius}
            fill="none" stroke={colorSet.stroke}
            strokeWidth={config.strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-700 ease-out"
          />
        </svg>

        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`font-bold ${colorSet.text} ${config.fontSize}`}>
            {displayValue}
          </span>
          {maxValue && !showPercentage && (
            <span className="text-xs text-neutral-400">/ {maxValue}</span>
          )}
        </div>
      </div>

      {label && (
        <p className={`mt-2 font-medium text-neutral-700 dark:text-neutral-300 text-center ${config.labelSize}`}>
          {label}
        </p>
      )}

      {band && (
        <span className={`mt-1.5 px-3 py-0.5 rounded-full font-medium ${colorSet.badgeBg} ${colorSet.badgeText} ${config.bandSize}`}>
          {band}
        </span>
      )}
    </div>
  );
};

GaugeMeter.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  maxValue: PropTypes.number,
  label: PropTypes.string,
  band: PropTypes.string,
  color: PropTypes.oneOf(['green', 'blue', 'yellow', 'amber', 'orange', 'red', 'gray', 'purple']),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  showPercentage: PropTypes.bool,
  className: PropTypes.string
};

export default GaugeMeter;
